import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { GlobalNotificationService } from '../../services/globalnotification.service';
import { RTAApiClient } from '../../services/rta-api.service';
import { takeUntil, debounceTime, Observable, Subject, of, finalize, combineLatest, take, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { RequestActions } from '../../store/action/request.action';
import { AccountState } from '../../store/state/account.state';
import { OptionState } from '../../store/state/options.state';
import { CreateCutterOverrideRequestDto, CutterOverrideRequest, IORD_Bit, PatchORDBitDto, PatchORDOrderDto, RTAOptions } from '../../interfaces/request.interface';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import * as allIcons from "@progress/kendo-svg-icons";

declare var CURRENT_USER_EMAIL: string;

@Component({
    selector: 'app-fixedcutter-data',
    templateUrl: './fixedcutter-data.component.html',
    styleUrl: './fixedcutter-data.component.css',
    standalone: false
})
export class FixedcutterDataComponent {
  @Output() sizeChange = new EventEmitter<number>();
  @Input() bitInitial?: PatchORDBitDto;
  @Input() draftId?: number;
  @Input() ProductLine$?: Observable<string>;
  @Input() readonly: boolean = false;
  @Input() excluded: boolean = false;
  @Output() nomenclaturePendingChanged = new EventEmitter<boolean>();

  public icons = allIcons;
  public appFixedCutterData!: FormGroup;
  public nomenclatureOverrideForm!: FormGroup;
  public nomenclatureSubmitting = false;
  public nomenclatureMessage: string | null = null;
  public nomenclatureError: string | null = null;
  public nomenclaturePending = false;
  public pendingOverrideRequest: CutterOverrideRequest | null = null;
  public nomenclatureCancelling = false;
  public inputModeDecimal: { [key: string]: string } = { inputmode: "decimal" };
  private typePattern = /^(CS|CX|EM|ES|FC|FM|FS|FX|GT|HD|HX|MM|SF)([DGETi]?)([0-9])([234568])(D?W?I?R?M?U?)(K?B?H?(?:HE)?O?F?T?E?)(s?)([0-9]?)$/;
  private readonly fallbackTypePattern = this.typePattern;
  private nomenclatureOverrideApproved = false;
  
  public rtaOptions$!: Observable<RTAOptions[]>;
  private destroy$ = new Subject<void>();
  private excluded$ = new BehaviorSubject<boolean>(false);
  public basicData$!: Observable<PatchORDOrderDto>;
  public hardfacingOptions$!: Observable<any[]>;
  public electronics_Data$!: Observable<any[]>;
  public uppersection_Data$!: Observable<any[]>;
  public breaker_Data$!: Observable<any[]>;
  public fieldRegex$!: Observable<any[]>;
  public overrideFamilies$ = of<{ id: number; name: string }[]>([]);
  public levelOptions = [
        { id: 2, name: '2 - No Cutters' },
        { id: 3, name: '3' },
        { id: 4, name: '4 - No Cutters' },
        { id: 5, name: '5 - Detached U/S' },
        { id: 6, name: '6 - Full Build' },
        { id: 7, name: '7 - Finished Mold' }
    ];

  // FormGroup options
  public CerebroInfluenceTypes$!: Observable<any[]>;


  //form formatting
  public decimals = 3;
  public format = "n3";

  constructor(private fb: FormBuilder, private store: Store, private route: ActivatedRoute,
    private globalNotificationService: GlobalNotificationService, private rtaApi: RTAApiClient) {
    this.appFixedCutterData = this.fb.group({
      size: [null],
      coreSize: [null],
      type: [null, [Validators.required, Validators.pattern(this.typePattern)]],
      level: [null],
      gaugeLength: [null],
      BitUsedOculusData: [null],
      BitUsedCerebroData: [null],
      CerebroInfluencer: [null],
      BitCerebroInfluenceDescription: [null],
      sleeve: [false],
      onePiece: [false],
      hardfacing: [null]
    });

    this.nomenclatureOverrideForm = this.fb.group({
      nomenclature: [null, Validators.required],
      justification: [null, Validators.required],
      requesterEmail: [null, Validators.email],
      overrideCode: [null]
    });
    this.updateTypeValidators();
  }

  private updateTypeValidators(): void {
    const ctrl = this.appFixedCutterData.get('type');
    if (!ctrl) {
      return;
    }
    const validators = [Validators.required];
    if (!this.nomenclatureOverrideApproved) {
      validators.push(Validators.pattern(this.typePattern));
    }
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private applyTypePatternFromConfig(list: any[] | null | undefined): void {
    if (!list) {
      return;
    }
    const match = list.find(x => {
      const name = (x?.name ?? x?.Name ?? '').toString();
      return name.toLowerCase() === 'fixedcutterbittype';
    });
    const patternValue = match?.value ?? match?.Value;
    if (patternValue) {
      try {
        this.typePattern = new RegExp(patternValue);
      } catch {
        this.typePattern = this.fallbackTypePattern;
      }
      this.updateTypeValidators();
    }
  }

  applyNomenclatureCode(): void {
    this.nomenclatureMessage = null;
    this.nomenclatureError = null;

    if (!this.draftId) {
      this.nomenclatureError = 'No order id available.';
      return;
    }

    const code = (this.nomenclatureOverrideForm.get('overrideCode')?.value || '').toString().trim();
    if (!code) {
      this.nomenclatureOverrideForm.get('overrideCode')?.markAsTouched();
      this.nomenclatureError = 'Enter a code to validate.';
      return;
    }

    this.nomenclatureSubmitting = true;
    this.rtaApi.validateOverrideCode({ orderId: this.draftId, code })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.nomenclatureSubmitting = false)
      )
      .subscribe({
        next: res => {
          if (res?.status) {
            const info = (res as any)?.info as any;
            const requestType = (info?.requestType ?? info?.RequestType ?? '').toString().toLowerCase();
            if (requestType && requestType !== 'nomenclature') {
              this.nomenclatureError = 'Code is not for nomenclature override.';
              return;
            }
            this.nomenclatureOverrideApproved = true;
            this.updateTypeValidators();
            this.nomenclatureMessage = 'Override code accepted. Type format validation disabled.';
            this.nomenclatureOverrideForm.get('overrideCode')?.reset();
            // Clear pending state since approval code was applied
            this.pendingOverrideRequest = null;
            this.nomenclaturePending = false;
            this.nomenclaturePendingChanged.emit(false);
          } else {
            this.nomenclatureError = res?.message ?? 'Invalid code.';
          }
        },
        error: err => {
          this.nomenclatureError = err?.message ?? 'Failed to validate code.';
        }
      });
  }

  public yesno: Array<any> = [
    { value: false, name: "No" },
    { value: true, name: "Yes" }
  ];
  public uppersectionList: any[] = [];
  public importedUpperDrawing?: string;
  public breakerList: any[] = [];
  public importedBreaker?: string;
  ngOnInit(): void {
    this.CerebroInfluenceTypes$ = this.store.select(OptionState.cerebroInfluenceTypes);
    this.electronics_Data$ = this.store.select(OptionState.electronics);
    this.uppersection_Data$ = this.store.select(OptionState.uppersection);
    this.breaker_Data$ = this.store.select(OptionState.bitBreakers);
    this.fieldRegex$ = this.store.select(OptionState.fieldRegex);
    
    // Combine body materials with excluded input to reactively filter hardfacing options
    this.hardfacingOptions$ = combineLatest([
      this.store.select(OptionState.bodyMaterial),
      this.excluded$
    ]).pipe(
      map(([materials, isExcluded]) => (materials ?? []).filter(m => {
        // Must be HARDFACING location
        const location = (m?.location ?? m?.Location ?? '').toString().toUpperCase();
        if (location !== 'HARDFACING') {
          return false;
        }
        
        // Check obsolete status: include if not obsolete, or if obsolete AND excluded is true
        const isObsolete = m?.obsolete ?? m?.Obsolete ?? false;
        
        return !isObsolete || (isObsolete && isExcluded);
      }))
    );
    this.uppersection_Data$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.uppersectionList = list ?? [];
        this.updateImportedUpperDrawing();
      });
    this.breaker_Data$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
    this.breakerList = list ?? [];
    this.updateImportedBreaker();
    });
    this.fieldRegex$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.applyTypePatternFromConfig(list);
      });

    this.appFixedCutterData.valueChanges.pipe(
      debounceTime(200),
      takeUntil(this.destroy$)
    ).subscribe(bitChanges => {
      this.store.dispatch(new RequestActions.PatchRequestDraft({
        draftId: this.draftId,
        changes: { bit: bitChanges }
      }));
      this.store.dispatch(new RequestActions.PatchRequest({
         bit: bitChanges 
      }));
      this.sizeChange.emit(bitChanges.size);
    });

    // Auto-fill requester email from the logged-in user
    if (CURRENT_USER_EMAIL) {
      this.nomenclatureOverrideForm.patchValue({ requesterEmail: CURRENT_USER_EMAIL });
    }

    // Load pending override status
    this.loadOverrideStatus();
  }

  /** Fetches override requests for this order and updates pending state */
  public loadOverrideStatus(): void {
    if (!this.draftId) {
      return;
    }
    this.rtaApi.getOverrideRequests(this.draftId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(requests => {
        const pending = (requests ?? []).find(r =>
          (r.requestType ?? '').toLowerCase() === 'nomenclature' &&
          (r.status ?? '').toLowerCase() === 'pending'
        );
        this.pendingOverrideRequest = pending ?? null;
        this.nomenclaturePending = !!pending;
        this.nomenclaturePendingChanged.emit(this.nomenclaturePending);

        // If approved, unlock the type validator
        const approved = (requests ?? []).find(r =>
          (r.requestType ?? '').toLowerCase() === 'nomenclature' &&
          (r.status ?? '').toLowerCase() === 'approved'
        );
        if (approved) {
          this.nomenclatureOverrideApproved = true;
          this.updateTypeValidators();
        }
      });
  }

  /** Cancels/retracts a pending nomenclature override request */
  public cancelOverrideRequest(): void {
    if (!this.pendingOverrideRequest?.id) {
      return;
    }
    this.nomenclatureCancelling = true;
    this.nomenclatureMessage = null;
    this.nomenclatureError = null;
    this.rtaApi.cancelOverrideRequest(this.pendingOverrideRequest.id)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => this.nomenclatureCancelling = false)
      )
      .subscribe({
        next: res => {
          if (res?.status) {
            this.pendingOverrideRequest = null;
            this.nomenclaturePending = false;
            this.nomenclaturePendingChanged.emit(false);
            this.nomenclatureMessage = 'Override request has been retracted.';
            this.globalNotificationService.showSuccessNotification('Override request retracted.');
          } else {
            this.nomenclatureError = res?.message ?? 'Unable to retract override request.';
          }
        },
        error: err => {
          this.nomenclatureError = err?.message ?? 'Failed to retract override request.';
        }
      });
  }

  submitNomenclatureOverride(): void {
    this.nomenclatureMessage = null;
    this.nomenclatureError = null;

    if (!this.draftId) {
      this.nomenclatureError = 'No order id available.';
      return;
    }

    if (this.nomenclatureOverrideForm.invalid) {
      this.nomenclatureOverrideForm.markAllAsTouched();
      return;
    }

    const { nomenclature, justification, requesterEmail } = this.nomenclatureOverrideForm.value;
    const payload: CreateCutterOverrideRequestDto = {
      requestType: 'Nomenclature',
      nomenclatureRequested: nomenclature,
      justification,
      requesterEmail
    };

    this.nomenclatureSubmitting = true;
    this.rtaApi.createOverrideRequest(this.draftId, this.rtaApi.$CURRENT_USER_ID, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const msg = typeof (res as any)?.status === 'string' ? (res as any).status : 'Override request submitted.';
          this.nomenclatureMessage = msg;
          this.nomenclatureOverrideForm.get('nomenclature')?.reset();
          this.nomenclatureOverrideForm.get('justification')?.reset();
          this.loadOverrideStatus();
        },
        error: err => {
          this.nomenclatureError = err?.message ?? 'Failed to submit override request.';
        }
      })
      .add(() => this.nomenclatureSubmitting = false);
  }
  ngOnChanges(changes: SimpleChanges): void {
    // Handle readonly state changes
    if (changes['readonly']) {
      if (this.readonly) {
        this.appFixedCutterData?.disable();
        this.nomenclatureOverrideForm?.disable();
      } else {
        this.appFixedCutterData?.enable();
        this.nomenclatureOverrideForm?.enable();
      }
    }

    // Push excluded input changes to the BehaviorSubject for reactive filtering
    if (changes['excluded']) {
      this.excluded$.next(!!this.excluded);
    }
    
    const ch = changes['bitInitial'];
    if (ch && ch.currentValue) {
      const b = ch.currentValue as PatchORDBitDto;
      this.appFixedCutterData.patchValue({
        size: b.size,
        coreSize: b.coreSize,
        type: b.type,
        level: b.level,
        gaugeLength: b.gaugeLength,
        CerebroInfluencer: b.isBHSModified,
        BitCerebroInfluenceDescription: b.hardfacing,
        sleeve: b.sleeve,
        onePiece: b.onePiece,
        hardfacing: b.hardfacing

      }, { emitEvent: false });
      if (b.size !== undefined && b.size !== null) {
        this.sizeChange.emit(b.size);
      }
      this.updateImportedUpperDrawing();
      this.updateImportedBreaker();
      
      // Re-apply readonly state after patching values
      if (this.readonly) {
        this.appFixedCutterData?.disable();
      }
    }
  }
  onUpdateSize(value: number): void {
    this.sizeChange.emit(value);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateImportedUpperDrawing(): void {
    const upperId = this.bitInitial?.uppersection_Id;
    if (!upperId || !this.uppersectionList?.length) {
      this.importedUpperDrawing = undefined;
      return;
    }
    const match = this.uppersectionList.find(u =>
      u?.id === upperId ||
      u?.Id === upperId ||
      u?.ID === upperId ||
      u?.uppersection_Id === upperId ||
      u?.uppersectionId === upperId
    );
    const drawing = match?.drawingNumber ?? match?.DrawingNumber ?? match?.drawingnumber ?? match?.drawing_no;
    this.importedUpperDrawing = drawing ? drawing.toString() : undefined;
  }

  private updateImportedBreaker(): void {
    const breakerId = this.bitInitial?.breaker_Id;
    if (!breakerId || !this.breakerList?.length) {
      this.importedBreaker = undefined;
      return;
    }
    const match = this.breakerList.find(b =>
      b?.id === breakerId ||
      b?.Id === breakerId ||
      b?.ID === breakerId ||
      b?.bitBreaker_Id === breakerId ||
      b?.bitBreakerId === breakerId
    );
    const name = match?.drawingNumber ?? match?.DrawingNumber ?? match?.drawingNumber1 ?? match?.Name ?? match?.name ?? match?.description ?? match?.Description;
    this.importedBreaker = name ? name.toString() : undefined;
  }
}
